export interface MenuItem {
  text: string;
  link?: string;
  icon?: string;
  heading?: boolean;
  submenu?: MenuItem[];
  elink?: string;
  target?: string;
  alert?: string;
  label?: string;
  translate?: string;
  claim?: string;
  privilege?: string;
  state?: string;
  tierRestricted?: boolean; // If true, check tier visibility
  hideWhileImpersonating?: boolean; // If true, drop from the nav during impersonation
  // Other links this row owns, matched like `link`. A query param on one of these is a
  // requirement, so '/channels?role=service' owns /channels/42?role=service (CHAT-1155).
  activeLinks?: string[];
}

export const MenuItems = {
  // Main navigation

  // All tenants + impersonate. /partners is a real SuperAdmin-gated route as of
  // CHAT-856 — the destination that lets the header's inline tenant <select> retire.
  // CHAT-959: hidden for the duration of an impersonation session. This row IS the
  // tenant switcher, and reaching it from inside somebody else's account invites
  // switching tenant on top of an impersonation instead of stopping it first. Stop
  // (in the app bar) returns you to your own account, and this row returns with it.
  Partners: {
    text: 'Partners',
    link: '/partners',
    icon: 'icon-globe-alt',
    privilege: 'SuperAdmin',
    hideWhileImpersonating: true,
  },

  // Deprecated from the nav (CHAT-855); the bot list is no longer a nav destination.
  Bots: {
    text: 'Bots',
    link: '/',
    icon: 'icon-bubbles',
    privilege: 'User',
  },

  Connectors: {
    text: 'Connectors',
    link: '/connectors',
    icon: 'icon-link',
    privilege: 'User',
    state: 'ready',
  },

  Users: {
    text: 'Admin Users',
    link: '/users',
    icon: 'icon-people',
    privilege: 'User',
  },

  ChatQueue: {
    text: 'Chat Queue',
    link: '/chat-queue',
    icon: 'icon-earphones-alt',
    privilege: 'User',
  },

  // Deprecated from the nav (CHAT-855); Knowledge Base moves to the header '?' in CHAT-856.
  Help: {
    text: 'Knowledge Base',
    elink: ':help_uri',
    target: 'blank',
    icon: 'icon-info',
  },

  // Bot-specific navigation
  // Unused since the bot-scope menu was retired (CHAT-855).
  Back: {
    text: 'Main Menu',
    link: '/',
    icon: 'icon-arrow-left',
  },

  BotAdmin: {
    text: 'Admin',
    link: '/configuration/admin',
    icon: 'icon-briefcase',
    privilege: 'SuperAdmin',
  },

  BotGeneral: {
    text: 'General',
    link: '/general',
    icon: 'icon-info',
    state: 'ready',
    privilege: 'Admin',
    tierRestricted: true,
  },

  BotDashboard: {
    text: 'Dashboard',
    link: '/launchpad',
    icon: 'icon-rocket',
  },

  // CHAT-1155: the Channels page is split into these three leaves under MenuGroups.Channels.
  // A channel's own screens (/channels/:id, /channels/new...) carry ?role=client|service,
  // and activeLinks is how that query picks the row - see selectedItemId. Connected
  // Tenants moved here from the second tab of Connectors.
  ClientChannels: {
    text: 'Client Channels',
    link: '/channels/client',
    activeLinks: ['/channels?role=client'],
    state: 'ready',
  },

  ServiceChannels: {
    text: 'Service Channels',
    link: '/channels/service',
    activeLinks: ['/channels?role=service'],
    state: 'ready',
  },

  ConnectedTenants: {
    text: 'Connected Tenants',
    link: '/channels/tenants',
    state: 'ready',
  },

  // CHAT-857: renamed from 'Dialogs' so it no longer shares a name with the group it sits
  // in. TierService builds its starter-hidden list from MenuItems.BotDialogs.text rather
  // than a literal, so the tier gate followed this rename on its own.
  BotDialogs: {
    text: 'Dialog Builder',
    link: '/dialogs',
    icon: 'icon-speech',
    privilege: 'Dialog',
    tierRestricted: true,
  },

  BotDialogs2: {
    text: 'Dialog IDE',
    link: '/dialogs2',
    icon: 'icon-layers',
    privilege: 'SuperAdmin',
    tierRestricted: true,
  },

  BotCommands: {
    text: 'Commands',
    link: '/commands',
    icon: 'icon-magic-wand',
    state: 'ready',
  },

  BotPlaybooks: {
    text: 'Playbooks',
    link: '/playbooks',
    icon: 'icon-book-open',
    privilege: 'SuperAdmin',
    tierRestricted: true,
  },

  BotActions: {
    text: 'Actions',
    link: '/actions',
    icon: 'icon-target',
    privilege: 'Admin',
    state: 'ready',
    tierRestricted: true,
  },

  // Deprecated from the nav (CHAT-855); /audience route unchanged.
  BotAudience: {
    text: 'Audience',
    link: '/audience',
    icon: 'icon-microphone',
    state: 'ready',
    privilege: 'Audience',
    tierRestricted: true,
  },

  // Deprecated from the nav (CHAT-855); /conversations route unchanged.
  BotConversations: {
    text: 'Conversations',
    link: '/conversations',
    icon: 'icon-bubbles',
    state: 'ready',
    privilege: 'SuperAdmin',
  },

  // Deprecated from the nav (CHAT-855); /forms route unchanged.
  BotForms: {
    text: 'Forms',
    link: '/forms',
    icon: 'icon-list',
    privilege: 'Forms',
    state: 'ready',
    tierRestricted: true,
  },

  BotOrganization: {
    text: 'Account',
    link: '/organization',
    icon: 'icon-organization',
    state: 'ready',
  },

  BotQueries: {
    text: 'Queries',
    link: '/queries',
    icon: 'icon-speech',
    privilege: 'Queries',
    state: 'ready',
  },

  BotReports: {
    text: 'Chat Logs',
    link: '/reports',
    icon: 'icon-grid',
    state: 'ready',
  },

  BotResources: {
    text: 'Resources',
    link: '/resources',
    icon: 'icon-notebook',
    privilege: 'User',
    state: 'ready',
    tierRestricted: true,
  },

  BotDialogMessages: {
    text: 'Dialog Messages',
    link: '/dialog-messages',
    icon: 'icon-bubble',
    privilege: 'User',
    state: 'ready',
    tierRestricted: true,
  },

  BotRoles: {
    text: 'Roles',
    link: '/roles',
    icon: 'icon-key',
    privilege: 'Admin',
    tierRestricted: true,
  },

  BotTags: {
    text: 'Tags',
    link: '/tags',
    icon: 'icon-tag',
    privilege: 'Admin',
    tierRestricted: true,
  },

  BotIdentity: {
    text: 'Bot Identity',
    link: '/settings/identity',
    icon: 'icon-settings',
    state: 'ready',
  },

  BotLiveChat: {
    text: 'Live Chat',
    link: '/settings/live-chat',
    icon: 'icon-settings',
    state: 'ready',
  },

  BotDialogEvents: {
    text: 'Dialog Events',
    link: '/dialogevents',
    icon: 'icon-pencil',
    privilege: 'Developer',
  },

  BotEvents: {
    text: 'System Logs',
    link: '/events',
    icon: 'icon-clock',
  },

  BotTriggers: {
    text: 'Triggers',
    link: '/triggers',
    icon: 'icon-energy',
    privilege: 'Admin',
  },

  // Superseded by Connectors (CHAT-855) - same /connectors page, the User gate is the broader and correct one for an admin portal.
  BotConnectors: {
    text: 'Connectors',
    link: '/connectors',
    icon: 'icon-link',
    privilege: 'Admin',
  },

  // Deprecated from the nav (CHAT-857); /overrides route unchanged.
  BotDialogOverrides: {
    text: 'Overrides',
    link: '/overrides',
    icon: 'icon-layers',
    privilege: 'SuperAdmin',
  },

  BotWatchers: {
    text: 'Watchers',
    link: '/watchers',
    icon: 'icon-eye',
    privilege: 'Developer',
    state: 'ready',
  },
};

// Group parents (CHAT-855). Toggle-only rows: a submenu and an icon, deliberately no link,
// which the sidebar template already renders as an expand/collapse header.
//
// Only the Admin group carries a privilege of its own. The others are ungated on purpose -
// their children keep their existing privilege / tierRestricted values and self-filter, and
// MenuService drops a group whose children all filter out.
export const MenuGroups = {
  Admin: {
    text: 'Admin',
    icon: 'icon-briefcase',
    privilege: 'SuperAdmin',
    submenu: [
      MenuItems.BotAdmin,
      MenuItems.BotGeneral,
      MenuItems.BotResources,
      MenuItems.BotWatchers,
    ],
  },

  // CHAT-1155: was a single Channels leaf, in this same position with this same icon.
  Channels: {
    text: 'Channels',
    icon: 'icon-globe',
    submenu: [
      MenuItems.ClientChannels,
      MenuItems.ServiceChannels,
      MenuItems.ConnectedTenants,
    ],
  },

  // CHAT-857: the old single 'Dialogs' group is split in two. It had eight children spanning
  // two unrelated jobs, and its own name collided with the 'Dialogs' leaf inside it - the
  // sidebar rendered 'Dialogs > Dialogs'. Authoring lives under Dialogs and the rules that
  // decide which dialog runs, and for whom, live under Routing.
  //
  // The group keeps the 'Dialogs' name and the LEAF is what moved: it is 'Dialog Builder'
  // now, so parent and child no longer collide. That is the other way round from how the
  // split first resolved it, and it is the better way - 'Dialogs' is what the section is,
  // and the builder is one thing you do in it.
  //
  // Membership is a straight re-parenting: every child keeps its existing privilege /
  // tierRestricted / state verbatim. No row gained or lost a gate here.
  //
  // The KEYS stay ConversationFlow / ConversationRouting - they say what the groups are for,
  // and nothing outside this file reads them.
  ConversationFlow: {
    text: 'Dialogs',
    icon: 'icon-speech',
    submenu: [
      MenuItems.BotDialogMessages,
      MenuItems.BotDialogs,
      MenuItems.BotDialogs2,
    ],
  },

  ConversationRouting: {
    text: 'Routing',
    icon: 'icon-shuffle',
    submenu: [
      MenuItems.BotTriggers,
      MenuItems.BotRoles,
      MenuItems.BotTags,
      MenuItems.BotDialogEvents,
    ],
  },

  BotSkills: {
    text: 'Bot Skills',
    icon: 'icon-magic-wand',
    submenu: [
      MenuItems.BotCommands,
      MenuItems.BotActions,
      MenuItems.BotPlaybooks,
      MenuItems.BotQueries,
    ],
  },

  Logs: {
    text: 'Logs',
    icon: 'icon-docs',
    submenu: [MenuItems.BotReports, MenuItems.BotEvents],
  },

  Settings: {
    text: 'Settings',
    icon: 'icon-settings',
    submenu: [
      MenuItems.BotIdentity,
      MenuItems.BotLiveChat,
      MenuItems.BotOrganization,
      MenuItems.Users,
      MenuItems.Connectors,
    ],
  },
};
